/**
 * Collapse visibility helpers.
 *
 * A collapsed container node stays visible (it shows a count chip); only its
 * descendants are hidden. A node is hidden when ANY ancestor in its collapse
 * chain is collapsed — this makes nested collapse "just work" with independent
 * per-node flags (collapsing a parent hides everything under it regardless of
 * the children's own flags).
 *
 * Derived render nodes (agent tool / model / phantom / terminal) ARE hidden
 * here too, via {@link shouldHideRenderNode}: each carries an explicit pointer
 * to its base/owning node (`data.parentAgentId`, `parentId`, or
 * `data.baseNodeId`), so when that base is under a collapsed container the
 * derived node hides with it. What this module does NOT do is reflow or reposition
 * anything — collapse is a pure hide (visibility only), never a layout mutation.
 *
 * These helpers use local structural types (only the fields they read) rather
 * than the richer hook-owned `GraphNode`/`GraphEdge`, so `utils` never depends
 * on `hooks` — the actual graph node/edge types are structurally assignable.
 */

/** Minimal node shape the collapse helpers read. */
interface CollapseNode {
  id: string;
  data?: { scopeParentId?: string | undefined } | undefined;
}

/** Minimal edge shape the collapse helpers read. */
interface CollapseEdge {
  source: string;
  target: string;
}

/**
 * Build a child→parent map for collapse purposes.
 *
 * - Action nodes point at their scope parent (`data.scopeParentId`), which is
 *   the canvas id of the enclosing If/Switch/Foreach/Until container. (Bare
 *   `Scope` is not a collapse parent in v1 — see clusterConfig.)
 * - `scope-container-*` background nodes (the ForEach/Until loop-body halo)
 *   point at their owning container action so they hide with it.
 */
export const buildCollapseParentMap = (nodes: CollapseNode[]): Map<string, string> => {
  const parentById = new Map<string, string>();
  for (const node of nodes) {
    const scopeParentId = node.data?.scopeParentId;
    if (scopeParentId) {
      parentById.set(node.id, scopeParentId);
      continue;
    }
    // scope-container-<scopedId> belongs to action-<scopedId>
    if (node.id.startsWith('scope-container-')) {
      parentById.set(node.id, `action-${node.id.slice('scope-container-'.length)}`);
    }
  }
  return parentById;
};

/**
 * Shared empty result for the common "nothing collapsed" case. Returning a
 * stable reference (instead of a fresh `new Set()` each call) keeps the
 * `hiddenNodeIds` memo — and therefore the node/edge sync effect that depends
 * on it — identity-stable across recomputes. Churning a new empty Set every
 * render re-fires that effect, which makes React Flow re-measure and re-fit the
 * canvas mid-settle (it can leave a large fan-out's trigger framed off-screen).
 * Callers only ever read this set (`.has`/`.size`), so sharing one instance is
 * safe. Typed as `ReadonlySet` so an accidental `.add()`/`.clear()` on the
 * shared singleton is a compile-time error rather than a global-state footgun.
 */
export const EMPTY_HIDDEN_NODE_IDS: ReadonlySet<string> = new Set<string>();

/**
 * Returns the set of node ids that are hidden because an ancestor is collapsed.
 * The collapsed containers themselves are NOT included (they remain visible).
 */
export const getHiddenNodeIds = (
  nodes: CollapseNode[],
  collapsedNodes: Set<string>
): ReadonlySet<string> => {
  if (collapsedNodes.size === 0) return EMPTY_HIDDEN_NODE_IDS;

  const parentById = buildCollapseParentMap(nodes);
  const hidden = new Set<string>();

  for (const node of nodes) {
    // Walk up the parent chain; hidden if any ancestor is collapsed.
    // Guard against cycles with a visited set (defensive — the graph is a DAG).
    let current = parentById.get(node.id);
    const visited = new Set<string>();
    while (current && !visited.has(current)) {
      if (collapsedNodes.has(current)) {
        hidden.add(node.id);
        break;
      }
      visited.add(current);
      current = parentById.get(current);
    }
  }

  return hidden.size === 0 ? EMPTY_HIDDEN_NODE_IDS : hidden;
};

/**
 * An edge is hidden when either endpoint is hidden. The container's own
 * outgoing edge to its next sibling survives automatically because neither the
 * container (collapsed, not hidden) nor the sibling is in the hidden set.
 *
 * Terminal add-button edges also hide correctly here: their `source` is the
 * base node id, so a hidden base hides the edge without any id parsing.
 */
export const shouldHideRenderEdge = (
  edge: CollapseEdge,
  hiddenNodeIds: ReadonlySet<string>
): boolean => {
  if (hiddenNodeIds.size === 0) return false;
  return hiddenNodeIds.has(edge.source) || hiddenNodeIds.has(edge.target);
};

/**
 * Whether a rendered node — including derived tool/model/phantom/terminal
 * nodes — should be hidden because it (or its owning agent/base node) is under
 * a collapsed container.
 *
 * Derived nodes carry an explicit pointer to their base node:
 * - tool nodes → `data.parentAgentId`
 * - model/child nodes → React Flow `parentId`
 * - terminal phantom nodes → `data.baseNodeId`
 * so hiding is an exact-id lookup with no fragile string parsing.
 */
export const shouldHideRenderNode = (
  node: {
    id: string;
    parentId?: string | undefined;
    data?: { parentAgentId?: string | undefined; baseNodeId?: string | undefined } | undefined;
  },
  hiddenNodeIds: ReadonlySet<string>
): boolean => {
  if (hiddenNodeIds.size === 0) return false;
  if (hiddenNodeIds.has(node.id)) return true;
  const baseNodeId = node.data?.baseNodeId;
  if (baseNodeId && hiddenNodeIds.has(baseNodeId)) return true;
  const parent = node.parentId ?? node.data?.parentAgentId;
  return parent ? hiddenNodeIds.has(parent) : false;
};
